import { min } from "mathjs";
import { scaleConfig, SvgIconKey } from "@/@corefabriccanvas/icons";
import { get, set } from "@/data/ionicStorage";
import { PageIcon, PageIconType, UserContentType } from "@/types";

// Kullanıcı notu için arabirim tanımlaması
export interface UserNote {
  id: string;
  userId: string;
  noteType: UserContentType;
  content?: string;
  bookId: number;
  pageId: number;
  pageItemId?: string;
  x: number;
  y: number;
  canvasWidth: number;
  canvasHeight: number;
}

// Kullanıcı notunu kaydeder
export const saveUserNote = async (userNote: UserNote): Promise<PageIcon> => {
  try {
    let userNotes: UserNote[] = (await get("userNotes")) as UserNote[];
    if (!userNotes) {
      userNotes = [];
    }
    const isUpdate =
      userNotes.filter((note) => note.id === userNote.id).length > 0;
    if (isUpdate) {
      userNotes = userNotes.map((note) =>
        note.id === userNote.id
          ? {
              ...note,
              content: userNote.content,
              x: userNote.x,
              y: userNote.y,
              canvasWidth: userNote.canvasWidth,
              canvasHeight: userNote.canvasHeight,
            }
          : note
      );
    } else {
      userNotes.push(userNote);
    }
    await set("userNotes", userNotes);

    const noteIconType = getNoteIcon(userNote.noteType);
    const pageIconType = getPageIconType(userNote.noteType);

    const iconScale = scaleConfig[noteIconType];
    const scale = userNote.canvasHeight / 849 / iconScale;
    const pageIcon: PageIcon = {
      id: userNote.id,
      pageNumber: 0,
      x: userNote.x,
      y: userNote.y,
      type: pageIconType,
      icon: noteIconType,
      filePath: userNote.content,
      entityId: 0,
      leftMargin: 0,
      topMargin: 0,
      width: 0,
      height: 0,
      correctAnswer: "",
      iconScale: scale,
      isUserContent: true,
      content: userNote.content
    };
    return pageIcon;
  } catch (err) {
    return Promise.reject(`Error: ${err}`);
  }
};

export const updateNotePosition = async (
  id: string,
  x: number,
  y: number,
  canvasWidth: number,
  canvasHeight: number
): Promise<void> => {
  try {
    let userNotes: UserNote[] = (await get("userNotes")) as UserNote[];
    if (!userNotes) {
      userNotes = [];
    }
    const isUpdate = userNotes.filter((note) => note.id === id).length > 0;
    if (isUpdate) {
      userNotes = userNotes.map((note) =>
        note.id === id
          ? {
              ...note,
              x: x,
              y: y,
              canvasWidth: canvasWidth,
              canvasHeight: canvasHeight,
            }
          : note
      );
      await set("userNotes", userNotes);
    }
  } catch (err) {
    return Promise.reject(`Error: ${err}`);
  }
};

// Kullanıcı notlarını alır
export const getUserNotes = async (
  bookId: number,
  pageId: number,
  pageItemId: string,
  userId: string,
  canvasWidth: number,
  canvasHeight: number
): Promise<PageIcon[]> => {
  try {
    const userNotes: UserNote[] = (await get("userNotes")) as UserNote[];
    if (!userNotes) {
      return [];
    }

    const noteList = userNotes.filter(
      (x) => x.bookId === bookId && x.pageId === pageId
    );
    const noteIcons: PageIcon[] = noteList.map((note) => {
      const ratio = min(
        canvasWidth / note.canvasWidth,
        canvasHeight / note.canvasHeight
      );
      const noteIconType = getNoteIcon(note.noteType);

      const iconScale = scaleConfig[noteIconType];
      const scale = note.canvasHeight / 849 / iconScale;
      const pageIconType = getPageIconType(note.noteType);
      return {
        id: note.id,
        pageNumber: 0,
        x: note.x * ratio,
        y: note.y * ratio,
        type: pageIconType,
        icon: noteIconType,
        entityId: 0,
        leftMargin: 0,
        topMargin: 0,
        width: 0,
        height: 0,
        correctAnswer: "",
        iconScale: scale,
        filePath: note.content,
        isUserContent: true,
        content: note.content,
      };
    });

    return noteIcons;
  } catch (err) {
    return Promise.reject(`Error: ${err}`);
  }
};

// kullanıcı notunu idye göre
export const getUserNoteById = async (
  id: string
): Promise<UserNote | undefined> => {
  try {
    const userNotes: UserNote[] = (await get("userNotes")) as UserNote[];
    const note = userNotes.find((x) => x.id === id);
    return note;
  } catch (err) {
    return Promise.reject(`Error: ${err}`);
  }
};

export const getNoteIcon = (noteType: UserContentType): SvgIconKey => {
  switch (noteType) {
    case UserContentType.Sound:
      return SvgIconKey.SoundIcon;
    case UserContentType.Video:
      return SvgIconKey.Video;
    case UserContentType.Text:
      return SvgIconKey.Text;
    case UserContentType.Image:
      return SvgIconKey.Image;
    case UserContentType.Drawing:
      return SvgIconKey.Drawing;
    case UserContentType.Link:
      return SvgIconKey.Link;
    case UserContentType.File:
      return SvgIconKey.File;
    default:
      return SvgIconKey.Note;
  }
};

export const getPageIconType = (noteType: UserContentType): PageIconType => {
  switch (noteType) {
    case UserContentType.Sound:
      return PageIconType.Sound;
    case UserContentType.Video:
      return PageIconType.Video;
    case UserContentType.Text:
      return PageIconType.TextNote;
    case UserContentType.Image:
      return PageIconType.Image;
    case UserContentType.Drawing:
      return PageIconType.Drawing;
    case UserContentType.Link:
      return PageIconType.Url;
    case UserContentType.File:
      return PageIconType.File;
    default:
      return PageIconType.Note;
  }
};

// Kullanıcı notunu siler
export const deleteUserNote = async (id: string): Promise<void> => {
  try {
    const datas: UserNote[] = (await get("userNotes")) as UserNote[];
    const newDatas = datas.filter((x) => x.id !== id);
    await set("userNotes", newDatas);
  } catch (err) {
    return Promise.reject(`Error: ${err}`);
  }
};
